import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {NatResidentBikeParkingUseApplyLocationState} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatResidentBikeParkingUseApplyForm from './resident/NatResidentBikeParkingUseApplyForm'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {
    NatPutBikeParkingUseApplyRequest,
    NatPutBikeParkingUseApplyResponse,
} from '../repository/NatPutBikeParkingUseApply'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentBikeParkingUseApplyLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    useArea: Nullable<string>
    useStartDate: Nullable<string>
    userFamilyName: Nullable<string>
    userFirstName: Nullable<string>
    userAttribute: Nullable<string>
    ownerFamilyName: Nullable<string>
    ownerFirstName: Nullable<string>
    bikeType: Nullable<string>
    bikeName: Nullable<string>
    bikeRegistrationNumber: Nullable<string>
    engineSize: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentBikeParkingUseApplyInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            useArea: null,
            useStartDate: null,
            userFamilyName: null,
            userFirstName: null,
            userAttribute: null,
            ownerFamilyName: null,
            ownerFirstName: null,
            bikeType: null,
            bikeName: null,
            bikeRegistrationNumber: null,
            engineSize: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, applicationRepo, eventEmitter} = this.props
        const {applyInfo, applyId} = this.props.location.state

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultUseArea = applyInfo?.useArea ?? null
        let defaultUseStartDate = applyInfo?.useStartDate ?? null
        let defaultUserFamilyName = applyInfo?.userFamilyName ?? null
        let defaultUserFirstName = applyInfo?.userFirstName ?? null
        let defaultUserAttribute = applyInfo?.userAttribute ?? null
        let defaultOwnerFamilyName = applyInfo?.ownerFamilyName ?? null
        let defaultOwnerFirstName = applyInfo?.ownerFirstName ?? null
        let defaultBikeType = applyInfo?.bikeType ?? null
        let defaultBikeName = applyInfo?.bikeName ?? null
        let defaultBikeRegistrationNumber = applyInfo?.bikeRegistrationNumber ?? null
        let defaultEngineSize = applyInfo?.engineSize ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const bikeParkingUseApplyDetail = await this.props.asyncCallFunction(
                    applicationRepo.getBikeParkingUseApplyForResident.bind(applicationRepo, condominiumCd, applyId),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (bikeParkingUseApplyDetail?.details) {
                    defaultUseArea = bikeParkingUseApplyDetail.details.useArea
                    defaultUseStartDate = bikeParkingUseApplyDetail.details.useStartDate
                    defaultUserFamilyName = bikeParkingUseApplyDetail.details.userFamilyName
                    defaultUserFirstName = bikeParkingUseApplyDetail.details.userFirstName
                    defaultUserAttribute = bikeParkingUseApplyDetail.details.userAttribute
                    defaultOwnerFamilyName = bikeParkingUseApplyDetail.details.ownerFamilyName
                    defaultOwnerFirstName = bikeParkingUseApplyDetail.details.ownerFirstName
                    defaultBikeType = bikeParkingUseApplyDetail.details.bikeType
                    defaultBikeName = bikeParkingUseApplyDetail.details.bikeName
                    defaultBikeRegistrationNumber = bikeParkingUseApplyDetail.details.bikeRegistrationNumber
                    defaultEngineSize = bikeParkingUseApplyDetail.details.engineSize
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            useArea: defaultUseArea,
            useStartDate: defaultUseStartDate,
            userFamilyName: defaultUserFamilyName,
            userFirstName: defaultUserFirstName,
            userAttribute: defaultUserAttribute,
            ownerFamilyName: defaultOwnerFamilyName,
            ownerFirstName: defaultOwnerFirstName,
            bikeType: defaultBikeType,
            bikeName: defaultBikeName,
            bikeRegistrationNumber: defaultBikeRegistrationNumber,
            engineSize: defaultEngineSize,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutBikeParkingUseApplyRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutBikeParkingUseApplyResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyBikeParkingUseApply.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentBikeParkingUseApplyLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        useArea: request.useArea,
                        useStartDate: request.useStartDate,
                        userFamilyName: request.userFamilyName,
                        userFirstName: request.userFirstName,
                        userAttribute: request.userAttribute,
                        ownerFamilyName: request.ownerFamilyName,
                        ownerFirstName: request.ownerFirstName,
                        bikeType: request.bikeType,
                        bikeName: request.bikeName,
                        bikeRegistrationNumber: request.bikeRegistrationNumber,
                        engineSize: request.engineSize,
                    },
                    applyId: this.props.location.state.applyId,
                }

                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/bikeParkingUses/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.location.state.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/bikeParkingUses/${this.props.location.state.applyId}/copy`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications/bikeParkingUses/agreement`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    バイク置場使用申込
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentBikeParkingUseApplyForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentBikeParkingUseApplyInputPage)
